import React, { useState, useEffect, useMemo } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import LinearProgress from '@mui/material/LinearProgress';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Stack,
  Chip,
  IconButton,
  Autocomplete,
} from '@mui/material';

import { MenuItem, Select, InputLabel, FormControl } from '@mui/material';

import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../../redux/snackbarSlice';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'; // Use DateTimePicker for date & time selection
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { getTypeColor } from '../../../../utils/nodeTypeMapper';
import { useTranslation } from 'react-i18next';

const AddInitiativeForm = ({
  selectedEnergyGroup,
  selectedInitiative,
  onClose,
  addInitiative,
  deleteInitiative,
  updateInitiative,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    state: null, // Initialize state
    completed: null, // Initialize completedDate
    baselineStart: null, // Initialize baselineStart
    baselineEnd: null, // Initialize baselineEnd
    comparisonStart: null, // Initialize comparisonStart
    comparisonEnd: null, // Initialize comparisonEnd
    preferredModel: 0,
    energyTreeNodes: [],
    energyGroupId: selectedEnergyGroup?.energyGroupId || null,
  });

  const dispatch = useDispatch();
  const { t } = useTranslation('energyxplorer-initiatives-tab');
  const [nodeSelectionMode, setNodeSelectionMode] = useState('seu'); // State to manage node selection mode

  // Initialize formData with selectedInitiative if it exists
  useEffect(() => {
    if (selectedInitiative) {
      setFormData({
        name: selectedInitiative.name || '',
        description: selectedInitiative.description || '',
        baselineStart: selectedInitiative.baselineStart || '',
        baselineEnd: selectedInitiative.baselineEnd || '',
        comparisonStart: selectedInitiative.comparisonStart || '',
        comparisonEnd: selectedInitiative.comparisonEnd || '',
        completed: selectedInitiative.completed || null,
        state: selectedInitiative.state || null,
        energyTreeNodes: selectedInitiative.nodes || [], // Initialize with selectedInitiative nodes
        preferredModel: selectedInitiative.preferredModel || 0,
        energyGroupId: selectedEnergyGroup?.energyGroupId || null,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        completed: null, // Initialize completedDate
        state: null, // Initialize state
        baselineStart: null, // Initialize baselineStart
        baselineEnd: null, // Initialize baselineEnd
        comparisonStart: null, // Initialize comparisonStart
        comparisonEnd: null, // Initialize comparisonEnd
        preferredModel: 0,
        energyTreeNodes: [],
        energyGroupId: selectedEnergyGroup?.energyGroupId || null,
      });
    }
  }, [selectedInitiative]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = async () => {
    try {
      const baselineStart = formData.baselineStart
        ? DateTime.fromISO(formData.baselineStart).toUTC().toISO()
        : null;
      const baselineEnd = formData.baselineEnd
        ? DateTime.fromISO(formData.baselineEnd).toUTC().toISO()
        : null;
      const comparisonStart = formData.comparisonStart
        ? DateTime.fromISO(formData.comparisonStart).toUTC().toISO()
        : null;
      const comparisonEnd = formData.comparisonEnd
        ? DateTime.fromISO(formData.comparisonEnd).toUTC().toISO()
        : null;

      const formDataWithOptions = {
        ...formData,
        energyTreeNodes: formData.energyTreeNodes, // Ensure this is an array of IDs
        completed: null,
        baselineStart,
        baselineEnd,
        comparisonStart,
        comparisonEnd,
        preferredModel: 0,
      };

      await addInitiative({
        accountId: selectedEnergyGroup.accountId,
        energyInitiative: formDataWithOptions,
      }).unwrap();

      dispatch(
        showSnackbar({ message: 'Initiativ tilføjet', severity: 'success' }),
      );
      onClose();
    } catch (error) {
      console.error('Failed to add initiative:', error);
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl under oprettelse af initiativ',
          severity: 'error',
        }),
      );
    }
  };

  const handleUpdate = async () => {
    try {
      await updateInitiative({
        accountId: selectedEnergyGroup.accountId,
        energyInitiativeId: selectedInitiative.id,
        updates: formData,
      }).unwrap();

      dispatch(
        showSnackbar({ message: 'Initiativ opdateret', severity: 'success' }),
      );
      onClose();
    } catch (error) {
      console.error('Failed to update initiative:', error);
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl under redigering af initiativ',
          severity: 'error',
        }),
      );
    }
  };

  const getLabel = (field) => {
    switch (field) {
      case 'baselineStart':
        return t('add_initiative_form.baseline_start.label');
      case 'baselineEnd':
        return t('add_initiative_form.baseline_end.label');
      case 'comparisonStart':
        return t('add_initiative_form.comparison_start.label');
      case 'comparisonEnd':
        return t('add_initiative_form.comparison_end.label');
      default:
        return field;
    }
  };

  const nodeGroupsByType = useMemo(() => {
    const map = new Map();
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      const type = node?.type;
      if (type) {
        if (!map.has(type)) map.set(type, []);
        map.get(type).push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const seuTypes = useMemo(
    () => Array.from(nodeGroupsByType.keys()),
    [nodeGroupsByType],
  );

  const isFormValid = useMemo(() => {
    return (
      formData.name.trim() !== '' &&
      formData.baselineStart !== null &&
      formData.energyTreeNodes.length > 0 &&
      formData.state !== null
    );
  }, [formData.name, formData.baselineStart, formData.energyTreeNodes]);

  const selectedNodes = useMemo(
    () =>
      formData.energyTreeNodes
        .map((id) =>
          selectedEnergyGroup.energyTreeNodes.find(
            (n) => n.energyTreeNodeId === id,
          ),
        )
        .filter(Boolean),
    [formData.energyTreeNodes, selectedEnergyGroup],
  );

  return (
    <Stack
      sx={{
        width: '100%',
        padding: 3,
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6">
          {selectedInitiative
            ? t('add_initiative_form.edit_initiative.label')
            : t('add_initiative_form.add_initiative.label')}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <TextField
          label={t('add_initiative_form.name.label')}
          name="name"
          variant="filled"
          required
          error={!formData.name}
          value={formData.name}
          onChange={handleInputChange}
          fullWidth
          sx={{
            '& .MuiFilledInput-root': {
              '&:before': { borderBottom: 'none' }, // Removes default underline
              '&:after': { borderBottom: 'none' }, // Removes active/focused underline
              '&:hover:before': { borderBottom: 'none !important' }, // Ensures no underline on hover
              borderRadius: 1,
              flexGrow: 2,
            },
          }}
        />
        <FormControl
          required
          error={!formData.state}
          variant="filled"
          sx={{ minWidth: 250 }}
          disableUnderline={true}
        >
          <InputLabel id="state-label">
            {t('add_initiative_form.state.label')}
          </InputLabel>
          <Select
            labelId="state-label"
            value={formData.state || ''}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, state: e.target.value }))
            }
            disableUnderline={true}
            sx={{
              '& .MuiFilledInput-root': {
                '&:before': { borderBottom: 'none' },
                '&:after': { borderBottom: 'none' },
                '&:hover:before': { borderBottom: 'none !important' },
              },
              borderRadius: 1,
            }}
          >
            <MenuItem value="Created">
              <Chip
                size="small"
                label="Dataopsamling"
                sx={{
                  color: '#FFF',
                  borderRadius: 1,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? '#ffb74d' : '#ff9800',
                }}
              />
            </MenuItem>
            <MenuItem value="Active">
              <Chip
                size="small"
                label="Igangværende"
                sx={{
                  color: '#FFF',
                  borderRadius: 1,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? '#64b5f6' : '#1976d2',
                }}
              />
            </MenuItem>
            <MenuItem value="Done">
              <Chip
                size="small"
                label="Afsluttet"
                sx={{
                  color: '#FFF',
                  borderRadius: 1,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? '#81c784' : '#4caf50',
                }}
              />
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
      <TextField
        label={t('add_initiative_form.description.label')}
        name="description"
        variant="filled"
        size="small"
        value={formData.description}
        onChange={handleInputChange}
        fullWidth
        multiline
        rows={5}
        sx={{
          mb: 2,
          '& .MuiFilledInput-root': {
            '&:before': { borderBottom: 'none' }, // Removes default underline
            '&:after': { borderBottom: 'none' }, // Removes active/focused underline
            '&:hover:before': { borderBottom: 'none !important' }, // Ensures no underline on hover

            borderRadius: 1,
          },
        }}
      />
      {nodeSelectionMode === 'node' && (
        <>
          <Autocomplete
            multiple
            options={selectedEnergyGroup?.energyTreeNodes || []}
            aria-required
            disableCloseOnSelect
            getOptionLabel={(option) => option.name}
            value={selectedNodes}
            onChange={(event, newValue) => {
              setFormData({
                ...formData,
                energyTreeNodes: newValue.map((node) => node.energyTreeNodeId), // Only store UnitIds
              });
            }}
            filterOptions={(options, state) =>
              options.filter((option) =>
                (typeof option === 'string' ? option : (option?.name ?? ''))
                  .toLowerCase()
                  .includes(state.inputValue.toLowerCase()),
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Nodes"
                variant="filled"
                required
                error={!formData.energyTreeNodes.length}
                placeholder="Select Units"
                disableUnderline
                sx={{
                  '& .MuiFilledInput-root': {
                    '&:before': { borderBottom: 'none' }, // Removes default underline
                    '&:after': { borderBottom: 'none' }, // Removes active/focused underline
                    '&:hover:before': { borderBottom: 'none !important' }, // Ensures no underline on hover
                    borderRadius: 1,
                    '& .MuiFilledInput-input': {
                      margin: '14px 14px 14px 14px',
                    },
                  },
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.energyTreeNodeId}
                  label={option.name}
                  {...getTagProps({ index })}
                  sx={{
                    backgroundColor: getTypeColor(option.type),
                    color: 'white',
                    borderRadius: 1,
                  }}
                />
              ))
            }
          />
          <Box display="flex" justifyContent="flex-start">
            <Button
              variant="text"
              onClick={() => {
                setNodeSelectionMode('seu');
              }}
            >
              {t('add_initiative_form.select_seu.label')}
            </Button>
          </Box>
        </>
      )}

      {nodeSelectionMode === 'seu' && (
        <>
          <Autocomplete
            multiple
            options={seuTypes}
            getOptionLabel={(type) => {
              const allOfType = nodeGroupsByType.get(type) || [];
              const selectedCount = allOfType.filter((node) =>
                formData.energyTreeNodes.includes(node.energyTreeNodeId),
              ).length;
              return `${type} ${selectedCount}/${allOfType.length}`;
            }}
            value={Array.from(
              new Set(
                formData.energyTreeNodes
                  .map((id) =>
                    selectedEnergyGroup.energyTreeNodes.find(
                      (n) => n.energyTreeNodeId === id,
                    ),
                  )
                  .filter(Boolean)
                  .map((node) => node.type),
              ),
            )}
            // onChange={(event, newSelectedTypes) => {
            //   const selectedNodes = newSelectedTypes
            //     .flatMap((type) => nodeGroupsByType.get(type) || [])
            //     .map((node) => node);

            //   setFormData((prev) => ({
            //     ...prev,
            //     energyTreeNodes: selectedNodes,
            //   }));
            // }}
            onChange={(event, newSelectedTypes) => {
              const selectedNodes = newSelectedTypes
                .flatMap((type) => nodeGroupsByType.get(type) || [])
                .map((node) => node.energyTreeNodeId); // ✅ ensure IDs only

              setFormData((prev) => ({
                ...prev,
                energyTreeNodes: selectedNodes,
              }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('add_initiative_form.seu_types.label')}
                variant="filled"
                placeholder="Vælg typer"
                required
                error={!formData.energyTreeNodes.length}
                sx={{
                  '& .MuiFilledInput-root': {
                    '&:before': { borderBottom: 'none' },
                    '&:after': { borderBottom: 'none' },
                    '&:hover:before': { borderBottom: 'none !important' },
                    borderRadius: 1,
                    '& .MuiFilledInput-input': {
                      margin: '14px 14px',
                    },
                  },
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((type, index) => {
                const allOfType = nodeGroupsByType.get(type) || [];
                const selectedCount = allOfType.filter((node) =>
                  formData.energyTreeNodes.includes(node.energyTreeNodeId),
                ).length;
                const percentage = Math.round(
                  (selectedCount / allOfType.length) * 100,
                );
                const color = getTypeColor(type);

                return (
                  <Box
                    key={type}
                    sx={{
                      position: 'relative',
                      display: 'inline-block',
                      mr: 1,
                    }}
                  >
                    <Chip
                      label={
                        <Typography>
                          {type} {selectedCount}/{allOfType.length}
                        </Typography>
                      }
                      color={
                        selectedCount === allOfType.length
                          ? 'success'
                          : 'warning'
                      }
                      variant="filled"
                      sx={{ borderRadius: 1, backgroundColor: color }}
                      {...getTagProps({ index })}
                    />
                    <LinearProgress
                      variant="determinate"
                      value={percentage}
                      sx={{
                        position: 'absolute',
                        bottom: -4,
                        left: 0,
                        right: 0,
                        height: 3,
                        borderRadius: 1,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor:
                            selectedCount === allOfType.length
                              ? '#2e7d32'
                              : '#ed6c02',
                        },
                      }}
                    />
                  </Box>
                );
              })
            }
          />
          <Box display="flex" justifyContent="flex-start">
            <Button
              variant="text"
              onClick={() => {
                setNodeSelectionMode('node');
              }}
            >
              {t('add_initiative_form.select_measurement_point.label')}
            </Button>
          </Box>
        </>
      )}

      <Grid container spacing={2}>
        {[
          'baselineStart',
          'baselineEnd',
          'comparisonStart',
          'comparisonEnd',
        ].map((field, index) => (
          <Grid item xs={6} key={index}>
            <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="da">
              <DateTimePicker
                label={getLabel(field)}
                value={
                  formData[field] ? DateTime.fromISO(formData[field]) : null
                }
                onChange={(newValue) =>
                  handleInputChange({
                    target: { name: field, value: newValue?.toISO() },
                  })
                }
                // maxDateTime={DateTime.now()} // Restrict future dates and times
                views={['year', 'month', 'day']} // Remove minutes
                format="dd/MM/yyyy" // Danish time format
                ampm={false} // 24-hour format
                slotProps={{
                  textField: {
                    variant: 'filled',
                    fullWidth: true,
                    required: field === 'baselineStart',
                    error: field === 'baselineStart' && !formData.baselineStart,
                    InputLabelProps: { shrink: true },
                    InputProps: {
                      disableUnderline: true,
                    },
                    sx: {
                      mb: 0,
                      '& .MuiFilledInput-root': {
                        '&:before': { borderBottom: 'none' }, // Removes default underline
                        '&:after': { borderBottom: 'none' }, // Removes active/focused underline
                        '&:hover:before': { borderBottom: 'none !important' }, // Ensures no underline on hover
                        borderRadius: 1,
                      },
                    },
                  },
                  popper: {
                    sx: {
                      '.MuiPickersCalendarHeader-label': {
                        textTransform: 'capitalize', // Ensure first letter is uppercase
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
        {selectedInitiative && (
          <Button variant="text" color="error" onClick={deleteInitiative}>
            {t('add_initiative_form.remove')}
          </Button>
        )}
        <Button
          variant="contained"
          disabled={!isFormValid}
          onClick={selectedInitiative ? handleUpdate : handleAdd}
        >
          {selectedInitiative
            ? t('add_initiative_form.save_changes.label')
            : t('add_initiative_form.create.label')}
        </Button>
      </Box>
    </Stack>
  );
};

export default AddInitiativeForm;
